import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import AdminHeader from './components/AdminHeader';
import ImageUpload from '../components/ImageUpload';
import apiService from '../services/apiService';

const StandingForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    played: 0,
    won: 0,
    drawn: 0,
    lost: 0,
    goalsFor: 0,
    goalsAgainst: 0,
    points: 0,
    isCurrentTeam: false
  });

  useEffect(() => {
    if (isEdit) {
      loadStanding();
    }
  }, [id, isEdit]);

  const loadStanding = async () => {
    try {
      const standing = await apiService.getStandingById(id);
      setFormData({
        name: standing.name,
        logo: standing.logo || '',
        played: standing.played,
        won: standing.won,
        drawn: standing.drawn,
        lost: standing.lost,
        goalsFor: standing.goalsFor,
        goalsAgainst: standing.goalsAgainst,
        points: standing.points,
        isCurrentTeam: standing.isCurrentTeam
      });
    } catch (error) {
      console.error('Error loading standing:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseInt(value) || 0 : value
    }));
  };

  const calculatePoints = () => {
    // Automatically calculate points: 3 for win, 1 for draw, 0 for loss
    const calculatedPoints = (formData.won * 3) + (formData.drawn * 1);
    setFormData(prev => ({
      ...prev,
      points: calculatedPoints
    }));
  };

  useEffect(() => {
    calculatePoints();
  }, [formData.won, formData.drawn]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit) {
        await apiService.updateStanding(id, formData);
      } else {
        await apiService.createStanding(formData);
      }
      navigate('/_panel-7c39f8/standings');
    } catch (error) {
      console.error('Error saving standing:', error);
      alert('შეცდომა გუნდის შენახვისას');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader 
        title={isEdit ? 'გუნდის რედაქტირება' : 'ახალი გუნდი'}
        subtitle={isEdit ? 'შეცვალეთ გუნდის ინფორმაცია ტურნირულ ცხრილში' : 'დაამატეთ ახალი გუნდი ტურნირულ ცხრილში'}
      >
        <button
          onClick={() => navigate('/_panel-7c39f8/standings')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-georgian"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          უკან
        </button>
      </AdminHeader>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg">
          <form onSubmit={handleSubmit} className="space-y-6 p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 font-georgian">
                    გუნდის სახელი *
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    placeholder="შეიყვანეთ გუნდის სახელი"
                  />
                </div>

                <ImageUpload
                  value={formData.logo}
                  onChange={(url) => setFormData(prev => ({ ...prev, logo: url }))}
                  category="logos"
                  label="გუნდის ლოგო"
                  required={false}
                />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="played" className="block text-sm font-medium text-gray-700 font-georgian">
                      თამაშებული
                    </label>
                    <input
                      type="number"
                      name="played"
                      id="played"
                      min="0"
                      value={formData.played}
                      onChange={handleChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    />
                  </div>

                  <div>
                    <label htmlFor="points" className="block text-sm font-medium text-gray-700 font-georgian">
                      ქულები (ავტომატური)
                    </label>
                    <input
                      type="number"
                      name="points"
                      id="points"
                      value={formData.points}
                      readOnly
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm bg-gray-50 font-georgian"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label htmlFor="won" className="block text-sm font-medium text-gray-700 font-georgian">
                      მოგება
                    </label>
                    <input
                      type="number"
                      name="won"
                      id="won"
                      min="0"
                      value={formData.won}
                      onChange={handleChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    />
                  </div>

                  <div>
                    <label htmlFor="drawn" className="block text-sm font-medium text-gray-700 font-georgian">
                      ფრე
                    </label>
                    <input
                      type="number"
                      name="drawn"
                      id="drawn"
                      min="0"
                      value={formData.drawn}
                      onChange={handleChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    />
                  </div>

                  <div>
                    <label htmlFor="lost" className="block text-sm font-medium text-gray-700 font-georgian">
                      წაგება
                    </label>
                    <input
                      type="number"
                      name="lost"
                      id="lost"
                      min="0"
                      value={formData.lost}
                      onChange={handleChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="goalsFor" className="block text-sm font-medium text-gray-700 font-georgian">
                      გატანილი
                    </label>
                    <input
                      type="number"
                      name="goalsFor"
                      id="goalsFor"
                      min="0"
                      value={formData.goalsFor}
                      onChange={handleChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    />
                  </div>

                  <div>
                    <label htmlFor="goalsAgainst" className="block text-sm font-medium text-gray-700 font-georgian">
                      მიღებული
                    </label>
                    <input
                      type="number"
                      name="goalsAgainst"
                      id="goalsAgainst"
                      min="0"
                      value={formData.goalsAgainst}
                      onChange={handleChange}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 font-georgian"
                    />
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    id="isCurrentTeam"
                    name="isCurrentTeam"
                    type="checkbox"
                    checked={formData.isCurrentTeam}
                    onChange={handleChange}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isCurrentTeam" className="ml-2 block text-sm text-gray-900 font-georgian">
                    ჩვენი გუნდია (FC ივერია)
                  </label>
                </div>

                {/* Stats Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-900 font-georgian mb-3">სტატისტიკა</h4>
                  <div className="grid grid-cols-2 gap-2 text-xs font-georgian">
                    <div className="flex justify-between">
                      <span>± (სხვაობა):</span>
                      <span className={formData.goalsFor - formData.goalsAgainst >= 0 ? 'text-green-600' : 'text-red-600'}>
                        {formData.goalsFor - formData.goalsAgainst >= 0 ? '+' : ''}{formData.goalsFor - formData.goalsAgainst}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>მოგების %:</span>
                      <span>{formData.played > 0 ? Math.round((formData.won / formData.played) * 100) : 0}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => navigate('/_panel-7c39f8/standings')}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 font-georgian"
              >
                გაუქმება
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 font-georgian"
              >
                {loading ? 'ინახება...' : (isEdit ? 'განახლება' : 'შენახვა')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default StandingForm;
